import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ApplicationSdk } from '../../../../core/sdk';
import { RequestStore, RequestItem } from '../';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { formatStringToDate } from '../../../../utils/helper';

const RequestOverview = observer(
    ({ sdk, currentStore }: { sdk: ApplicationSdk; currentStore: RequestStore }) => {
        const [dialogData, setDialogData] = useState<{
            comment: string;
            approver: any;
            request: any;
        } | null>(null);

        const getStatusInfo = (status: string) => {
            switch (status) {
                case 'approved':
                    return { label: 'Genehmigt', color: '#4CAF50', bgColor: '#E8F5E8' };
                case 'rejected':
                    return { label: 'Abgelehnt', color: '#F44336', bgColor: '#FFEBEE' };
                case 'pending':
                    return { label: 'In Bearbeitung', color: '#FF9800', bgColor: '#FFF3E0' };
                default:
                    return { label: 'Unbekannt', color: '#9E9E9E', bgColor: '#F5F5F5' };
            }
        };

        const renderRequestItem = (request: RequestItem) => {
            const statusInfo = getStatusInfo(request.status);
            const hasApprovalComment =
                request.approver?.comment && request.approver.comment.trim() !== '';
            const requestType = request.type;

            // Hintergrundfarbe basierend auf Status
            const getCardBackgroundColor = () => {
                switch (request.status) {
                    case 'approved':
                        return '#f8fff8'; // Sehr helles Grün
                    case 'rejected':
                        return '#fff5f5'; // Sehr helles Rot
                    case 'pending':
                        return '#fffbf0'; // Sehr helles Orange
                    default:
                        return '#ffffff'; // Standard Weiß
                }
            };

            const showComment = () => {
                setDialogData({
                    comment: request.approver?.comment || '',
                    approver: request.approver,
                    request: request,
                });
            };

            return (
                <Card
                    key={request.id}
                    header={requestType.name}
                    footer={
                        hasApprovalComment ? (
                            <div
                                style={{
                                    fontSize: '1rem',
                                    textAlign: 'center',
                                    padding: '0.25rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                <i
                                    className="pi pi-comment"
                                    style={{
                                        fontSize: '0.9rem',
                                        color: '#6c757d',
                                    }}
                                ></i>
                                Kommentar anzeigen
                            </div>
                        ) : undefined
                    }
                    pt={{
                        body: { style: { padding: 0 } },
                        content: { style: { padding: '0.25rem 0' } },
                        header: {
                            style: {
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                padding: '0 1rem',
                                display: 'flex',
                                alignItems: 'center',
                            },
                        },
                        footer: {
                            style: {
                                padding: '0 0.5rem',
                                borderTop: '1px solid #e9ecef',
                                fontWeight: '500',
                            },
                        },
                    }}
                    style={{
                        marginBottom: '0.5rem',
                        padding: '0.5rem',
                        backgroundColor: getCardBackgroundColor(),
                        border: `1px solid ${statusInfo.color}20`,
                        cursor: hasApprovalComment ? 'pointer' : 'default',
                    }}
                    onClick={hasApprovalComment ? showComment : undefined}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: '0.5rem',
                            minHeight: '60px',
                        }}
                    >
                        <div
                            style={{
                                width: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                flexShrink: 0,
                            }}
                        >
                            <i
                                className={`pi ${requestType.icon}`}
                                style={{
                                    fontSize: '1.5rem',
                                    color: '#6c757d',
                                }}
                            ></i>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'start',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '0.875rem',
                                    color: '#333',
                                    marginBottom: '0.25rem',
                                    lineHeight: '1.2',
                                    fontWeight: '600',
                                }}
                            >
                                {formatStringToDate(request.startDate || null)?.toLocaleDateString(
                                    'de-DE'
                                )}{' '}
                                bis{' '}
                                {formatStringToDate(request.endDate || null)?.toLocaleDateString(
                                    'de-DE'
                                )}
                            </div>
                            {request.approver && request.status !== 'pending' && (
                                <div
                                    style={{
                                        fontSize: '0.75rem',
                                        color: '#666',
                                        marginBottom: '0.25rem',
                                        fontWeight: '600',
                                    }}
                                >
                                    {request.status === 'approved'
                                        ? 'Genehmigt von:'
                                        : 'Abgelehnt von:'}{' '}
                                    {request.approver?.fullName || 'Unbekannt'}
                                </div>
                            )}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.375rem',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <span
                                    style={{
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '10px',
                                        fontSize: '0.8rem',
                                        fontWeight: '600',
                                        color: statusInfo.color,
                                        backgroundColor: statusInfo.bgColor,
                                        border: `1px solid ${statusInfo.color}`,
                                        lineHeight: '1',
                                    }}
                                >
                                    {statusInfo.label}
                                </span>
                            </div>
                        </div>
                    </div>
                </Card>
            );
        };

        return currentStore ? (
            <div style={{ margin: '0 auto', maxWidth: '400px' }}>
                {currentStore.requestList && currentStore.requestList.length > 0 ? (
                    <div style={{ maxWidth: '600px' }}>
                        {currentStore.requestList.map(request => renderRequestItem(request))}
                    </div>
                ) : (
                    <div
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: '#f8f9fa',
                            color: '#6c757d',
                            padding: '2rem',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                            textAlign: 'center',
                            zIndex: 1000,
                            width: '80%',
                            maxWidth: '500px',
                        }}
                    >
                        <i
                            className="pi pi-file-o"
                            style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}
                        ></i>
                        <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Keine Anträge vorhanden</h2>
                        <p style={{ marginTop: '1rem', fontSize: '1rem' }}>
                            Sie haben noch keine Anträge gestellt.
                        </p>
                    </div>
                )}
                <Dialog
                    header={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                color: '#495057',
                            }}
                        >
                            <i
                                className="pi pi-comment"
                                style={{ fontSize: '1.2rem', color: '#6c757d' }}
                            ></i>
                            <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>Kommentar</span>
                        </div>
                    }
                    closable={true}
                    visible={dialogData !== null}
                    style={{ width: '90%', maxWidth: '500px' }}
                    modal
                    onHide={() => setDialogData(null)}
                    pt={{
                        header: {
                            style: {
                                padding: '1.25rem 1.5rem 1rem 1.5rem',
                                borderBottom: '1px solid #dee2e6',
                            },
                        },
                        content: {
                            style: {
                                padding: '0.5rem',
                                lineHeight: '1.6',
                            },
                        },
                    }}
                >
                    <div
                        style={{
                            marginBottom: '0.5rem',
                            padding: '0.25rem',
                        }}
                    >
                        <div
                            style={{
                                fontWeight: '600',
                                color: '#495057',
                                fontSize: '0.9rem',
                                marginBottom: '0.25rem',
                            }}
                        >
                            {dialogData?.request?.status === 'approved'
                                ? 'Genehmigt von:'
                                : 'Abgelehnt von:'}{' '}
                            {dialogData?.approver?.fullName || 'Unbekannt'}
                        </div>
                        {dialogData?.approver?.approved && (
                            <div
                                style={{
                                    fontSize: '0.8rem',
                                    color: '#6c757d',
                                }}
                            >
                                {new Date(dialogData.approver.approved).toLocaleString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </div>
                        )}
                    </div>
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            padding: '1rem',
                            borderRadius: '6px',
                            border: '1px solid #e9ecef',
                        }}
                    >
                        <p
                            style={{
                                margin: 0,
                                fontSize: '1rem',
                                color: '#495057',
                                lineHeight: '1.6',
                            }}
                        >
                            {dialogData?.comment}
                        </p>
                    </div>
                </Dialog>
            </div>
        ) : null;
    }
);

const RequestNew = observer(
    ({ sdk, currentStore }: { sdk: ApplicationSdk; currentStore: RequestStore }) => {
        if (!currentStore?.requestEditor) return null;

        const { requestEditor } = currentStore;

        const handleSave = async () => {
            const saveDialog = await sdk.getDialogWait({
                title: 'Antrag wird gespeichert...',
                message: 'Bitte warten Sie einen Moment, während Ihr Antrag verarbeitet wird.',
                onConfirm: () => currentStore.createRequest(),
            });

            if (saveDialog.confirmed) {
                sdk.showSuccessToast('Antrag erfolgreich erstellt!');
                currentStore.quitRequestEditor(true);
            } else {
                sdk.showErrorToast(
                    'Fehler beim Erstellen des Antrags. Bitte versuchen Sie es später erneut.'
                );
            }
        };

        const handleCancel = () => {
            currentStore.quitRequestEditor();
        };

        return (
            <div
                style={{
                    margin: '0 auto',
                    maxWidth: '400px',
                    padding: '1rem',
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* Antragstyp */}
                    <div>
                        <label
                            htmlFor="requestType"
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '600',
                                color: '#495057',
                            }}
                        >
                            Antragstyp *
                        </label>
                        <Dropdown
                            id="requestType"
                            value={requestEditor.selectedType}
                            options={requestEditor.requestTypes}
                            onChange={e => requestEditor.setSelectedType(e.value)}
                            optionLabel="name"
                            placeholder="Bitte wählen Sie einen Antragstyp"
                            style={{ width: '100%' }}
                            disabled={!requestEditor.isTypeSelectable}
                            pt={{
                                trigger: {
                                    style: {
                                        padding: '0.75rem',
                                    },
                                },
                            }}
                        />
                    </div>

                    {/* Startdatum */}
                    <div>
                        <label
                            htmlFor="startDate"
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '600',
                                color: '#495057',
                            }}
                        >
                            Startdatum *
                        </label>
                        <Calendar
                            id="startDate"
                            value={requestEditor.startDate}
                            onChange={e => requestEditor.setStartDate(e.value as Date)}
                            dateFormat="dd.mm.yy"
                            placeholder="Startdatum auswählen"
                            style={{ width: '100%' }}
                            touchUI={true}
                            locale="de"
                            pt={{
                                input: {
                                    style: {
                                        padding: '0.75rem',
                                        width: '100%',
                                    },
                                },
                            }}
                        />
                    </div>

                    {/* Enddatum */}
                    <div>
                        <label
                            htmlFor="endDate"
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '600',
                                color: '#495057',
                            }}
                        >
                            Enddatum *
                        </label>
                        <Calendar
                            id="endDate"
                            value={requestEditor.endDate}
                            onChange={e => requestEditor.setEndDate(e.value as Date)}
                            dateFormat="dd.mm.yy"
                            placeholder="Enddatum auswählen"
                            touchUI={true}
                            locale="de"
                            minDate={requestEditor.startDate || undefined}
                            style={{ width: '100%' }}
                            pt={{
                                input: {
                                    style: {
                                        padding: '0.75rem',
                                        width: '100%',
                                    },
                                },
                            }}
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="comment"
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '600',
                                color: '#495057',
                            }}
                        >
                            Kommentar (optional)
                        </label>
                        <InputTextarea
                            id="comment"
                            value={requestEditor.comment}
                            onChange={e => requestEditor.setComment(e.target.value)}
                            placeholder="Zusätzliche Informationen..."
                            rows={4}
                            style={{
                                width: '100%',
                                resize: 'vertical',
                            }}
                            pt={{
                                root: {
                                    style: {
                                        padding: '0.75rem',
                                    },
                                },
                            }}
                        />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75rem',
                            marginTop: '1rem',
                        }}
                    >
                        <Button
                            label="Antrag stellen"
                            icon="pi pi-check"
                            onClick={handleSave}
                            disabled={!requestEditor.isValid}
                            style={{
                                padding: '0.75rem 1.5rem',
                                width: '100%',
                            }}
                        />
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            severity="secondary"
                            onClick={handleCancel}
                            style={{
                                padding: '0.75rem 1.5rem',
                                width: '100%',
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }
);

const RequestEmployeePage = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(RequestStore);
    }, [sdk]);

    const { currentStore }: { currentStore: RequestStore } = sdk;

    // Show a loading spinner if the store is loading
    if (currentStore?.isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                    flexDirection: 'column',
                    gap: '1rem',
                }}
            >
                <ProgressSpinner
                    style={{
                        width: '50px',
                        height: '50px',
                    }}
                    strokeWidth="4"
                    animationDuration="1s"
                />
                <div
                    style={{
                        color: '#6c757d',
                        fontSize: '1rem',
                        textAlign: 'center',
                    }}
                >
                    Daten werden geladen...
                </div>
            </div>
        );
    }

    return currentStore?.requestEditor ? (
        <RequestNew
            sdk={sdk}
            currentStore={currentStore}
        />
    ) : (
        <RequestOverview
            sdk={sdk}
            currentStore={currentStore}
        />
    );
});

export default RequestEmployeePage;
